// filepath: src/components/Contact.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  styled 
} from "@mui/material";
import { LocationOn, Email, Phone } from "@mui/icons-material";
import { motion } from "framer-motion";

// --- AI Kernel Imports ---
import { useKernel } from "./Shortener/hooks/AIKernel";
import { useFrustrationSensor } from "./Shortener/hooks/useFrustrationSensor";

// --- Framer Motion Components ---
const MotionPaper = styled(motion(Paper))({});
const MotionTextField = styled(motion(TextField))({});
const MotionButton = styled(motion(Button))({});
const MotionBox = styled(motion(Box))({});
const MotionTypography = styled(motion(Typography))({});

// --- ADVANCED ANIMATION VARIANTS ---
const sectionStagger = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { staggerChildren: 0.1, delayChildren: 0.1 } 
  },
};

const itemEnter = {
  hidden: { y: 20, opacity: 0, scale: 0.95 },
  visible: { 
    y: 0, opacity: 1, scale: 1, 
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } 
  },
};

const headerTitle = {
    hidden: { y: -50, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const headerSubtitle = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } },
};

const textFieldFocus = {
  focused: { 
    borderColor: "#d7b879", 
    boxShadow: "0px 0px 8px rgba(215, 184, 121, 0.28)",
    scale: 1.01,
    transition: { duration: 0.2 }
  },
  unfocused: {
    borderColor: "rgba(0,0,0,0.23)",
    boxShadow: "none",
    scale: 1,
    transition: { duration: 0.2 }
  }
};


const Contact = () => {
  // --- AI Engine Integration ---
  const { agentState, reportBehavior } = useKernel();
  useFrustrationSensor(); // 🧠 Watch for form-filling frustration

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState({
    name: "", email: "", subject: "", message: "",
  });
  const [focusedField, setFocusedField] = useState(null); 

  // 🧠 AI Actuator: Simplify UI if cognitive load is high
  const shouldSimplify = agentState.ui.simplifyAnimations;

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    alert(t("contact.form.send")); 
    
    // 🧠 AI Sensor: Track successful high-value conversion
    reportBehavior('lead_generation_success', 10, 'interest');
    reportBehavior('form_completion', 1, 'behavior');

    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <MotionBox
      initial={shouldSimplify ? "visible" : "hidden"}
      animate="visible"
      variants={sectionStagger} 
      className={agentState.theme} // 🧠 Apply Dynamic AI Theme
      sx={{
        minHeight: "100vh",
        background: agentState.theme === "theme-cinematic-dark"
          ? "radial-gradient(ellipse at 50% 0%, rgba(173,135,72,0.12), transparent 55%), #0b0c0f"
          : "linear-gradient(135deg, #e0f7fa, #e8eaf6, #f3e5f5)",
        backgroundSize: "400% 400%",
        // Kill background animation if user is frustrated
        animation: shouldSimplify ? "none" : "gradientAnimation 15s ease infinite",
        pt: { xs: 4, md: 8 }, 
        pb: { xs: 4, md: 8 },
        "& @keyframes gradientAnimation": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 5, md: 8 }}>
          <MotionTypography 
            variant={isMobile ? "h4" : "h3"} 
            fontWeight="bold" 
            gutterBottom 
            variants={headerTitle}
          >
            {t("contact.title")}
          </MotionTypography>
          <MotionTypography 
            variant={isMobile ? "body1" : "h6"} 
            color="text.secondary"
            variants={headerSubtitle}
          >
            {t("contact.subtitle")}
          </MotionTypography>
        </Box>

        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
          
          {/* Contact Information Block */}
          <Grid item xs={12} md={5}>
            <MotionPaper 
                elevation={8} 
                sx={{ p: { xs: 3, md: 5 }, height: "100%", borderRadius: 3 }}
                variants={itemEnter} 
            >
              <MotionTypography variant="h5" fontWeight="bold" mb={3} color="primary.dark" variants={itemEnter}>
                {t("contact.info")}
              </MotionTypography>

              <MotionBox display="flex" alignItems="flex-start" mb={4} variants={itemEnter}>
                <LocationOn sx={{ color: "primary.main", mr: 2, fontSize: 28, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {t("contact.nameAddress")}
                  </Typography>
                  <Box component="address" sx={{ fontStyle: 'normal' }}>
                    <Typography variant="body2" color="text.secondary">
                      L.P.Harisha Lakshan Warnakulasuriya
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Malabe, Sri Lanka
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sri Lanka 10115
                    </Typography>
                  </Box>
                </Box>
              </MotionBox>

              <MotionBox display="flex" alignItems="flex-start" mb={4} variants={itemEnter}>
                <Email sx={{ color: "primary.main", mr: 2, fontSize: 28, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {t("contact.email")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {/* 🧠 AI Sensor: Track direct outreach intent */}
                    <a href="mailto:unicornprofessionalbay@gmail.com" 
                       onClick={() => reportBehavior('direct_email_outreach', 5, 'interest')}
                       style={{ color: theme.palette.text.secondary, textDecoration: 'none' }}>
                        unicornprofessionalbay@gmail.com
                    </a>
                  </Typography>
                </Box>
              </MotionBox>

              <MotionBox display="flex" alignItems="flex-start" variants={itemEnter}>
                <Phone sx={{ color: "primary.main", mr: 2, fontSize: 28, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {t("contact.phone")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {/* 🧠 AI Sensor: Track direct outreach intent */}
                    <a href="tel:+94750788368" 
                       onClick={() => reportBehavior('direct_phone_outreach', 5, 'interest')}
                       style={{ color: theme.palette.text.secondary, textDecoration: 'none' }}>
                        +94-75-0788368
                    </a>
                  </Typography>
                </Box>
              </MotionBox>
            </MotionPaper>
          </Grid>

          {/* Contact Form Block */}
          <Grid item xs={12} md={7}>
            <MotionPaper elevation={8} sx={{ p: { xs: 3, md: 5 }, height: "100%", borderRadius: 3 }} variants={itemEnter}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Name */}
                  <Grid item xs={12} sm={6}>
                    <MotionTextField
                      fullWidth
                      label={t("contact.form.name")}
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => { setFocusedField("name"); reportBehavior('form_interaction', 0.5, 'behavior'); }}
                      onBlur={() => setFocusedField(null)}
                      variant="outlined"
                      required
                      size="medium"
                      variants={itemEnter} 
                      animate={!shouldSimplify && focusedField === "name" ? "focused" : "unfocused"} 
                      custom={theme.palette.primary.main} 
                      initial="unfocused"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
                          borderColor: "transparent", 
                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main, 
                            borderWidth: "2px", 
                          },
                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.light,
                          },
                          "& fieldset": {
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.23)", 
                          }
                        },
                      }}
                    />
                  </Grid>
                  {/* Email */}
                  <Grid item xs={12} sm={6}>
                    <MotionTextField
                      fullWidth
                      label={t("contact.form.email")}
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      variant="outlined"
                      required
                      size="medium"
                      variants={itemEnter}
                      animate={!shouldSimplify && focusedField === "email" ? "focused" : "unfocused"}
                      custom={theme.palette.primary.main}
                      initial="unfocused"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
                          borderColor: "transparent",
                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.light,
                          },
                           "& fieldset": {
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.23)",
                          }
                        },
                      }}
                    />
                  </Grid>
                  {/* Subject */}
                  <Grid item xs={12}>
                    <MotionTextField
                      fullWidth
                      label={t("contact.form.subject")}
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("subject")}
                      onBlur={() => setFocusedField(null)}
                      variant="outlined"
                      required
                      size="medium"
                      variants={itemEnter}
                      animate={!shouldSimplify && focusedField === "subject" ? "focused" : "unfocused"}
                      custom={theme.palette.primary.main}
                      initial="unfocused"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
                          borderColor: "transparent",
                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.light,
                          },
                           "& fieldset": {
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.23)",
                          }
                        },
                      }}
                    />
                  </Grid>
                  {/* Message */}
                  <Grid item xs={12}>
                    <MotionTextField
                      fullWidth
                      label={t("contact.form.message")}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("message")}
                      onBlur={() => setFocusedField(null)}
                      variant="outlined"
                      multiline
                      rows={isMobile ? 5 : 6}
                      required
                      variants={itemEnter}
                      animate={!shouldSimplify && focusedField === "message" ? "focused" : "unfocused"}
                      custom={theme.palette.primary.main}
                      initial="unfocused"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
                          borderColor: "transparent",
                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.light,
                          },
                           "& fieldset": {
                            border: "1px solid",
                            borderColor: "rgba(0,0,0,0.23)",
                          }
                        },
                      }}
                    />
                  </Grid>
                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <MotionButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      // 🧠 AI Actuator: Highlight CTA if high-intent user
                      className={agentState.ui.highlightCTAs ? 'cta-highlight-glow' : ''}
                      whileHover={!shouldSimplify ? { scale: 1.02, boxShadow: "0 4px 14px rgba(215, 184, 121, 0.24)" } : {}}
                      whileTap={!shouldSimplify ? { scale: 0.98 } : {}}
                      variants={itemEnter} 
                      sx={{ 
                        mt: 1, 
                        py: { xs: 1.5, md: 2 }
                      }}
                    >
                      {t("contact.form.submit")}
                    </MotionButton>
                  </Grid>
                </Grid>
              </form>
            </MotionPaper>
          </Grid>
        </Grid>
      </Container>
    </MotionBox>
  );
};

export default Contact;